import { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { getAiUsageHistory, DailyAiUsage } from '../api/aiUsage';
import { ApiError } from '../api/client';
import { Alert, Spinner } from '../shared/components/feedback';
import { LineChart } from '../shared/components/charts';

const fmt = (value: number) => value.toLocaleString('ko-KR');
export function AiUsageHistory({ days, refreshKey }: { days: number; refreshKey: string }) {
  const theme = useTheme();
  const [rows, setRows] = useState<DailyAiUsage[] | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    setRows(null); setError('');
    getAiUsageHistory(days).then((data) => { if (!cancelled) setRows(data); })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.description || e.message : 'AI 사용량 이력을 불러오지 못했습니다.'); });
    return () => { cancelled = true; };
  }, [days, refreshKey]);
  const sum = (key: 'totalTokens' | 'requests' | 'failedRequests' | 'blockedRequests') =>
    (rows || []).reduce((total, row) => total + row[key], 0);
  return <HistorySection aria-label="AI 사용량 이력">
    <h3>최근 {days}일 AI 사용량</h3>
    <Help>한국 시간 기준 · 전체 계정 합산 · 오늘은 조회 시점까지의 사용량입니다.</Help>
    {error ? <Alert tone="danger">{error}</Alert> : rows == null ? <Spinner aria-label="AI 이력 조회 중" /> : <>
      <Summary>
        <div><dt>기간 토큰 합계</dt><dd>{fmt(sum('totalTokens'))}</dd></div>
        <div><dt>AI 요청</dt><dd>{fmt(sum('requests'))}</dd></div>
        <div><dt>실패 요청</dt><dd>{fmt(sum('failedRequests'))}</dd></div>
        <div><dt>예산 차단</dt><dd>{fmt(sum('blockedRequests'))}</dd></div>
      </Summary>
      {rows.some((row) => row.recorded) ? <LineChart label="날짜별 AI 토큰 사용량" labels={rows.map((row) => row.date.slice(5))} series={[
        { name: '입력 토큰', color: theme.colors.primary, values: rows.map((row) => row.inputTokens) },
        { name: '출력 토큰', color: theme.colors.info, values: rows.map((row) => row.outputTokens) },
        { name: '확인 중 토큰', color: theme.colors.warning, values: rows.map((row) => row.reservedTokens) },
      ]} /> : <Help>선택한 기간에 보관된 AI 사용량 기록이 없습니다.</Help>}
      <HistoryDetails><summary>날짜별 상세 내역 보기 <span>최근 {days}일</span></summary><Help>표를 좌우로 스크롤하면 모든 항목을 확인할 수 있습니다.</Help><TableWrap tabIndex={0} role="region" aria-label="날짜별 AI 사용량 상세 표"><table>
        <caption>날짜별 AI 사용량 · 최신 날짜순</caption>
        <thead><tr>{['날짜', '총 토큰', '입력', '출력', '입력 캐시', '확인 중', '요청', '실패', '예산 차단', '이미지 요청', '분석 재사용'].map((label) => <th key={label} scope="col">{label}</th>)}</tr></thead>
        <tbody>{[...rows].reverse().map((row) => <tr key={row.date}>
          <th scope="row">{row.date}</th>
          {!row.recorded ? <td colSpan={10}>기록 없음</td> : <>
            {[row.totalTokens, row.inputTokens, row.outputTokens, row.cachedInputTokens, row.reservedTokens,
              row.requests, row.failedRequests, row.blockedRequests, row.imageRequests, row.cacheHits]
              .map((value, i) => <td key={i}>{fmt(value)}</td>)}
          </>}
        </tr>)}</tbody>
      </table></TableWrap></HistoryDetails>
      <Help>입력 캐시는 입력 토큰에 포함되므로 합계에 다시 더하지 않습니다. 확인 중인 토큰은 실제 사용량 합계에서 제외합니다.
        기록 없는 날짜는 그래프에서 0으로 표시합니다. 호출이 없었거나 보관된 기록이 없는 날이며, 과거 사용량을 복원한 값은 아닙니다.
        사용량 기록은 최대 90일 보관됩니다.</Help>
    </>}
  </HistorySection>;
}

const HistorySection = styled.section`
  min-width: 0;
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  h3 { font-size: 16px; letter-spacing: -0.02em; margin-bottom: 6px; }
`;
const Help = styled.p`
  font-size: 13px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 8px 0;
`;
const Summary = styled.dl`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin: 20px 0;
  div { min-width: 0; padding-left: 14px; border-left: 2px solid ${({ theme }) => theme.colors.border}; }
  dt { font-size: 13px; color: ${({ theme }) => theme.colors.textSecondary}; }
  dd { margin: 8px 0 0; font-size: 23px; font-weight: 750; letter-spacing: -0.03em; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
  @media (max-width: 640px) { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px 12px; }
`;
const HistoryDetails = styled.details`
  margin-top: 20px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 0 16px;
  summary { cursor: pointer; min-height: 48px; padding: 13px 0; font-size: 13px; font-weight: 650; }
  summary span { margin-left: 8px; font-weight: 400; color: ${({ theme }) => theme.colors.textSecondary}; }
  &[open] { padding-bottom: 12px; }
`;
const TableWrap = styled.div`
  overflow-x: auto;
  max-height: 420px;
  margin-top: 12px;
  overscroll-behavior-x: contain;
  table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 13px; font-variant-numeric: tabular-nums; }
  caption { text-align: left; padding: 10px 0; font-weight: 600; color: ${({ theme }) => theme.colors.textSecondary}; }
  th, td { white-space: nowrap; padding: 13px 12px; text-align: right; border-bottom: 1px solid ${({ theme }) => theme.colors.border}; }
  thead th { position: sticky; top: 0; z-index: 1; background: ${({ theme }) => theme.colors.surfaceMuted}; font-weight: 600; color: ${({ theme }) => theme.colors.textSecondary}; }
  th:first-child { position: sticky; left: 0; text-align: left; background: ${({ theme }) => theme.colors.surface}; border-right: 1px solid ${({ theme }) => theme.colors.border}; }
  thead th:first-child { z-index: 2; background: ${({ theme }) => theme.colors.surfaceMuted}; }
  tbody tr:hover td, tbody tr:hover th { background: ${({ theme }) => theme.colors.surfaceMuted}; }
`;
